import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { loadUserSites, setActiveSite, deleteSite, canAddSite, type UserSite, normalizeDomain } from "../services/siteService";
import { ingestAuthority44Audit } from "../services/authority44AuditService";

const selectedReportStorageKey = (domain: string) => `og01.selectedReportId.${domain}`;

const readStoredReportId = (domain?: string | null): string | null => {
  if (!domain || typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(selectedReportStorageKey(domain));
  } catch {
    return null;
  }
};

const writeStoredReportId = (domain: string | null | undefined, reportId: string | null) => {
  if (!domain || typeof window === "undefined") return;
  try {
    const key = selectedReportStorageKey(domain);
    if (reportId) window.sessionStorage.setItem(key, reportId);
    else window.sessionStorage.removeItem(key);
  } catch {
    /* ignore quota / private-mode failures */
  }
};

interface SiteContextType {
  sites: UserSite[];
  activeSite: UserSite | null;
  selectedReportId: string | null;
  loading: boolean;
  switchSite: (site: UserSite) => Promise<void>;
  selectReport: (reportId: string | null) => void;
  addNewSite: (domain: string, displayName?: string) => Promise<{ success: boolean; message?: string; ingesting?: boolean }>;
  removeSite: (site: UserSite) => Promise<boolean>;
  refreshSites: () => Promise<void>;
}

const SiteContext = createContext<SiteContextType>({
  sites: [],
  activeSite: null,
  selectedReportId: null,
  loading: true,
  switchSite: async () => { },
  selectReport: () => { },
  addNewSite: async () => ({ success: false }),
  removeSite: async () => false,
  refreshSites: async () => { },
});

export const useSiteContext = () => useContext(SiteContext);

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [sites, setSites] = useState<UserSite[]>([]);
  const [activeSite, setActiveSiteState] = useState<UserSite | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSites = useCallback(async () => {
    if (!userId) {
      setSites([]);
      setActiveSiteState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const loaded = await loadUserSites(userId);
      setSites(loaded);
      const active = loaded.find((s) => s.isActive) || loaded[0] || null;
      setActiveSiteState(active);
      setSelectedReportId(readStoredReportId(active?.domain));
    } catch (err) {
      console.warn("[SiteContext] Failed to load sites:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshSites();
  }, [refreshSites]);

  const switchSite = useCallback(async (site: UserSite) => {
    setActiveSiteState(site);
    setSelectedReportId(readStoredReportId(site.domain));
    setSites((prev) => prev.map((s) => ({ ...s, isActive: s.id === site.id })));
    if (!site.id.startsWith("derived-")) {
      await setActiveSite(userId, site.id);
    }
  }, [userId]);

  const selectReport = useCallback((reportId: string | null) => {
    setSelectedReportId(reportId);
    writeStoredReportId(activeSite?.domain, reportId);
  }, [activeSite?.domain]);

  const addNewSite = useCallback(async (domain: string, _displayName?: string): Promise<{ success: boolean; message?: string }> => {
    if (!userId) return { success: false, message: "Not authenticated" };

    const check = await canAddSite(userId);
    if (!check.allowed) return { success: false, message: check.message };

    const cleanDomain = normalizeDomain(domain);

    const existing = sites.find((s) => s.domain === cleanDomain);
    if (existing) {
      await switchSite(existing);
      return { success: true, message: "Site already exists — switched to it." };
    }

    const ingest = await ingestAuthority44Audit(cleanDomain);

    if (!ingest.success) {
      return { success: false, message: ingest.error || "Audit import failed. Site was not added." };
    }

    await refreshSites();

    return { success: true, message: "Audit imported and site added." };
  }, [userId, sites, switchSite, refreshSites]);

  const removeSite = useCallback(
    async (site: UserSite): Promise<boolean> => {
      if (!userId) return false;

      const success = await deleteSite(userId, site.id, site.domain);
      if (!success) return false;

      const loaded = await loadUserSites(userId);
      setSites(loaded);
      if (activeSite?.id === site.id || activeSite?.domain === site.domain) {
        const next = loaded[0] ?? null;
        setActiveSiteState(next);
        setSelectedReportId(readStoredReportId(next?.domain));
      }

      return true;
    },
    [userId, activeSite]
  );

  return (
    <SiteContext.Provider value={{ sites, activeSite, selectedReportId, loading, switchSite, selectReport, addNewSite, removeSite, refreshSites }}>
      {children}
    </SiteContext.Provider>
  );
};

